import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../App';

const SupervisorDashboard = () => {
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [supervisorData, setSupervisorData] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [promoSettings, setPromoSettings] = useState({});
  const [promoStats, setPromoStats] = useState({});
  
  // New state for customer dashboard features
  const [liveBids, setLiveBids] = useState([]);
  const [services, setServices] = useState([]);
  const [supplies, setSupplies] = useState([]);
  const [categories, setCategories] = useState({ services: [], products: [] });
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Fetch supervisor data and permissions
  useEffect(() => {
    fetchSupervisorData();
  }, []);

  const fetchSupervisorData = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      if (!token) {
        toast({
          title: "Authentication Error",
          description: "Please log in again",
          variant: "destructive",
        });
        return;
      }

      // Get current user data to check permissions
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        
        // Handle null/undefined values properly and handle admin users
        const cleanUserData = {
          ...userData,
          name: userData.name || (userData.userType === 'admin' ? 'Admin' : 'Supervisor'),
          email: userData.email || '',
          // Admin users get all supervisor permissions when accessing supervisor dashboard
          supervisorPermissions: userData.userType === 'admin' 
            ? ['view_users', 'manage_users', 'view_statistics', 'manage_promo_codes', 'manage_advertisements', 'export_data']
            : (userData.supervisorPermissions || [])
        };
        
        setSupervisorData(cleanUserData);
        setPermissions(cleanUserData.supervisorPermissions);
        
        // Set default tab based on permissions - admins default to overview
        if (Array.isArray(cleanUserData.supervisorPermissions) && cleanUserData.supervisorPermissions.length > 0) {
          if (userData.userType === 'admin' || cleanUserData.supervisorPermissions.includes('view_statistics')) {
            setActiveTab('overview');
          } else if (cleanUserData.supervisorPermissions.includes('view_users')) {
            setActiveTab('users');
          } else if (cleanUserData.supervisorPermissions.includes('manage_promo_codes')) {
            setActiveTab('promo');
          } else {
            setActiveTab('overview');
          }
        } else {
          // Fallback for admin users or users with no permissions
          setActiveTab('live-bids');
        }
        
        // Fetch additional data based on permissions
        await fetchDashboardData(token, cleanUserData.supervisorPermissions || []);
        
        // Fetch customer dashboard data for supervisor oversight
        await Promise.all([
          fetchLiveBids(),
          fetchServices(),
          fetchSupplies(),
          fetchCategories()
        ]);
        
      } else {
        toast({
          title: "Error",
          description: "Failed to load supervisor data",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error fetching supervisor data:', error);
      toast({
        title: "Error",
        description: "Failed to load supervisor dashboard",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDashboardData = async (token, userPermissions) => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    try {
      // Fetch stats if permitted
      if (userPermissions.includes('view_statistics')) {
        try {
          const statsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, { headers });
          if (statsResponse.ok) {
            const statsData = await statsResponse.json();
            setStats({
              totalUsers: statsData.totalUsers || 0,
              totalProviders: statsData.totalProviders || 0,
              totalServices: statsData.totalServices || 0,
              totalRevenue: statsData.totalRevenue || 0
            });
          }
        } catch (error) {
          console.error('Error fetching stats:', error);
        }
      }

      // Fetch users if permitted
      if (userPermissions.includes('view_users')) {
        try {
          const usersResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users?limit=10`, { headers });
          if (usersResponse.ok) {
            const usersData = await usersResponse.json();
            const cleanUsers = (usersData || []).slice(0, 10).map(user => ({
              ...user,
              name: user.name || 'Unknown',
              email: user.email || 'Unknown',
              userType: user.userType || 'customer',
              createdAt: user.createdAt || new Date().toISOString()
            }));
            setUsers(cleanUsers);
          }
        } catch (error) {
          console.error('Error fetching users:', error);
        }
      }

      // Fetch supervisors if permitted
      if (userPermissions.includes('manage_users')) {
        try {
          const supervisorsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/supervisors`, { headers });
          if (supervisorsResponse.ok) {
            const supervisorsData = await supervisorsResponse.json();
            setSupervisors(supervisorsData || []);
          }
        } catch (error) {
          console.error('Error fetching supervisors:', error);
        }
      }

      // Fetch promo data if permitted
      if (userPermissions.includes('manage_promo_codes')) {
        try {
          const promoSettingsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-settings`, { headers });
          if (promoSettingsResponse.ok) {
            const promoSettingsData = await promoSettingsResponse.json();
            setPromoSettings({
              featureEnabled: promoSettingsData.featureEnabled || false,
              durationMonths: promoSettingsData.durationMonths || 6
            });
          }
        } catch (error) {
          console.error('Error fetching promo settings:', error);
        }

        try {
          const promoStatsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/promo-stats`, { headers });
          if (promoStatsResponse.ok) {
            const promoStatsData = await promoStatsResponse.json();
            setPromoStats({
              generated: promoStatsData.generated || 0,
              used: promoStatsData.used || 0,
              active: promoStatsData.active || 0
            });
          }
        } catch (error) {
          console.error('Error fetching promo stats:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  // Fetch live bids data
  const fetchLiveBids = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/bids`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const bidsData = await response.json();
        setLiveBids(bidsData || []);
      }
    } catch (error) {
      console.error('Error fetching live bids:', error);
    }
  };

  // Fetch services data
  const fetchServices = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const servicesData = await response.json();
        setServices(servicesData || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  // Fetch supplies/products data
  const fetchSupplies = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const suppliesData = await response.json();
        setSupplies(suppliesData || []);
      }
    } catch (error) {
      console.error('Error fetching supplies:', error);
    }
  };

  // Fetch categories data
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const categoriesData = await response.json();
        setCategories(categoriesData || { services: [], products: [] });
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const exportData = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ exportType: 'users' }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `supervisor_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast({
          title: "Success",
          description: "Data exported successfully",
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to export data",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Error",
        description: "Failed to export data",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-teal-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-white text-xl mb-4">Loading Supervisor Dashboard...</div>
          <div className="text-white/70 text-sm">Please wait while we load your permissions and data</div>
        </div>
      </div>
    );
  }

  // Error state
  if (!supervisorData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-teal-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-xl mb-4">Error loading supervisor data</div>
          <button 
            onClick={fetchSupervisorData}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-blue-900 via-purple-900 to-teal-900" style={{width: '100vw', overflowX: 'hidden'}}>
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
                <span className="text-[10px] text-white/70">ECRA/ESA #7018534</span>
              </div>
              <span className="text-blue-400 text-sm font-semibold">SUPERVISOR</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-white/80 hidden sm:block">Welcome, {supervisorData?.name}</span>
              
              {/* Direct Navigation Dropdown - Consistent Style */}
              <div className="relative group">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Navigate
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-slate-800/95 backdrop-blur-lg border border-white/20 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible  z-50">
                  <div className="py-2">
                    <Link to="/dashboard" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      🏠 Main Dashboard
                    </Link>
                    {user?.userType === 'admin' && (
                      <Link to="/admin" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                        ⚙️ Admin Panel
                      </Link>
                    )}
                    <Link to="/inbox" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      📨 Messages
                    </Link>
                    <Link to="/profile" className="block px-4 py-2 text-white bg-white/5 transition-colors">
                      👤 Profile
                    </Link>
                    <div className="border-t border-white/20 my-2"></div>
                    <button 
                      onClick={logout}
                      className="block w-full text-left px-4 py-2 text-red-400 bg-red-500/20 transition-colors"
                    >
                      🚪 Logout
                    </button>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={logout}
                variant="outline" 
                className="border-red-400/50 text-red-400 bg-red-500/20"
              >
                🚪 Logout
              </Button>
              
              {/* Mobile Menu - Simple buttons for mobile */}
              <div className="sm:hidden flex items-center space-x-2">
                <Link to="/dashboard">
                  <Button variant="outline" size="sm" className="border-white/50 text-white bg-white/10">
                    🏠
                  </Button>
                </Link>
                <button 
                  onClick={logout}
                  className="border border-red-400/50 text-red-400 bg-red-500/20 px-2 py-1 rounded text-sm"
                >
                  🚪
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-black/10 backdrop-blur-sm border-b border-white/10 w-full">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                {user?.userType === 'admin' ? 'Admin Dashboard - Supervisor View' : 'Supervisor Dashboard'}
              </h1>
              <p className="text-white/80 text-sm sm:text-base mt-1">
                Welcome, {supervisorData?.name} | {user?.userType === 'admin' ? 'Admin Access' : `Permissions: ${Array.isArray(permissions) ? permissions.length : 0}`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {permissions.map((permission, index) => (
                <Badge key={index} variant="secondary" className="bg-blue-600/30 text-blue-200 border-blue-400/50">
                  {permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-6 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full bg-slate-800/90 backdrop-blur-lg border border-blue-600/30 p-1 mb-6" 
                    style={{ gridTemplateColumns: `repeat(${getAvailableTabs()?.length || 1}, minmax(0, 1fr))` }}>
            {getAvailableTabs().map((tab) => (
              <TabsTrigger
                key={tab.id}
                value={tab.id}
                className="text-white font-medium data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=inactive]:bg-slate-700 data-[state=inactive]:text-gray-300 hover:bg-blue-700/50   text-xs sm:text-sm py-2 px-3 rounded-md"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* Overview Tab */}
          {permissions.includes('view_statistics') && (
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-sm sm:text-base">Total Users</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl sm:text-3xl font-bold text-blue-300">{stats.totalUsers || 0}</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-sm sm:text-base">Providers</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl sm:text-3xl font-bold text-green-300">{stats.totalProviders || 0}</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-sm sm:text-base">Services</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl sm:text-3xl font-bold text-purple-300">{stats.totalServices || 0}</div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-sm sm:text-base">Revenue</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl sm:text-3xl font-bold text-yellow-300">${stats.totalRevenue || 0}</div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Users Tab */}
          {permissions.includes('view_users') && (
            <TabsContent value="users" className="space-y-6">
              <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
                <CardHeader className="pb-4">
                  <CardTitle className="text-white text-lg sm:text-xl">Recent Users</CardTitle>
                  <CardDescription className="text-blue-200">
                    Latest user registrations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {users.slice(0, 10).map((user, index) => (
                      <div key={index} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-slate-700/50 rounded-lg space-y-2 sm:space-y-0 border border-blue-600/20">
                        <div className="space-y-1">
                          <div className="font-medium text-white text-sm sm:text-base">{user.name}</div>
                          <div className="text-xs sm:text-sm text-blue-200">{user.email}</div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant={user.userType === 'provider' ? 'default' : 'secondary'} className={`text-xs ${user.userType === 'provider' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'}`}>
                            {user.userType}
                          </Badge>
                          <div className="text-xs text-blue-300">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Promo Codes Tab */}
          {permissions.includes('manage_promo_codes') && (
            <TabsContent value="promo" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
                  <CardHeader>
                    <CardTitle className="text-white">Promo Code Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className="text-blue-200">Generated:</span>
                      <span className="text-green-300 font-medium">{promoStats.generated || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className="text-blue-200">Used:</span>
                      <span className="text-yellow-300 font-medium">{promoStats.used || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className="text-blue-200">Active:</span>
                      <span className="text-purple-300 font-medium">{promoStats.active || 0}</span>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
                  <CardHeader>
                    <CardTitle className="text-white">Promo Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center text-sm sm:text-base">
                      <span className="text-blue-200">Feature Enabled:</span>
                      <Badge variant={promoSettings.featureEnabled ? "default" : "secondary"} className={promoSettings.featureEnabled ? "bg-green-600 text-white" : "bg-gray-600 text-white"}>
                        {promoSettings.featureEnabled ? "Yes" : "No"}
                      </Badge>
                    </div>
                    <div className="flex justify-between text-sm sm:text-base">
                      <span className="text-blue-200">Duration:</span>
                      <span className="text-blue-300 font-medium">{promoSettings.durationMonths || 6} months</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Export Tab */}
          {permissions.includes('export_data') && (
            <TabsContent value="export" className="space-y-6">
              <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
                <CardHeader>
                  <CardTitle className="text-white">Data Export</CardTitle>
                  <CardDescription className="text-blue-200">
                    Export platform data for analysis
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Button 
                    onClick={exportData}
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 min-h-[44px]"
                  >
                    📊 Export User Data
                  </Button>
                  <div className="text-xs sm:text-sm text-blue-300">
                    Exports will include user data based on your permissions
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Live Bids Tab - Available to all supervisors */}
          <TabsContent value="live-bids" className="space-y-6">
            <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
              <CardHeader>
                <CardTitle className="text-white">💼 Live Bids</CardTitle>
                <CardDescription className="text-blue-200">
                  Monitor active job bids across the platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {!Array.isArray(liveBids) || liveBids.length === 0 ? (
                    <div className="text-center py-8 text-white/60">
                      No active bids at the moment
                    </div>
                  ) : (
                    liveBids.slice(0, 10).map((bid, index) => (
                      <div key={index} className="p-4 bg-white/5 rounded-lg border border-blue-600/20">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="text-white font-medium">{bid.jobTitle || 'Job Posting'}</h4>
                            <p className="text-blue-200 text-sm mt-1">{bid.description || 'No description available'}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <span className="text-green-400 text-sm">💰 ${bid.bidAmount || 0}</span>
                              <span className="text-yellow-400 text-sm">⏱️ {bid.timeEstimate || 'N/A'}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-white/60 text-xs">{bid.providerName || 'Provider'}</div>
                            <div className="text-blue-300 text-xs">{bid.trade || 'General'}</div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Services Tab - Available to all supervisors */}
          <TabsContent value="services" className="space-y-6">
            <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
              <CardHeader>
                <CardTitle className="text-white">🔧 Platform Services</CardTitle>
                <CardDescription className="text-blue-200">
                  All services available on the platform
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Category Filter */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`px-3 py-1 rounded text-sm ${
                        selectedCategory === 'all' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-white/10 text-white/70 bg-white/10'
                      }`}
                    >
                      All Services
                    </button>
                    {Array.isArray(categories.services) && categories.services.map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-3 py-1 rounded text-sm ${
                          selectedCategory === category 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-white/10 text-white/70 bg-white/10'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  {Array.isArray(services) && services
                    .filter(service => selectedCategory === 'all' || service.category === selectedCategory)
                    .slice(0, 10).map((service, index) => (
                    <div key={index} className="p-4 bg-white/5 rounded-lg border border-blue-600/20">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="text-white font-medium">{service.title || service.name}</h4>
                          <p className="text-blue-200 text-sm mt-1">{service.description}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-green-400 text-sm">💰 ${service.price || 0}</span>
                            <span className="text-yellow-400 text-sm">⭐ {service.rating || 'N/A'}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white/60 text-xs">{service.providerName || 'Provider'}</div>
                          <div className="text-blue-300 text-xs">{service.category || 'General'}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!Array.isArray(services) || services.filter(service => selectedCategory === 'all' || service.category === selectedCategory).length === 0 && (
                    <div className="text-center py-8 text-white/60">
                      No services available in this category
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Supplies Tab - Available to all supervisors */}
          <TabsContent value="supplies" className="space-y-6">
            <Card className="bg-slate-800/80 backdrop-blur-lg border-blue-600/30">
              <CardHeader>
                <CardTitle className="text-white">🛒 Platform Supplies</CardTitle>
                <CardDescription className="text-blue-200">
                  Equipment and supplies available for purchase
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Category Filter */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`px-3 py-1 rounded text-sm ${
                        selectedCategory === 'all' 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-white/10 text-white/70 bg-white/10'
                      }`}
                    >
                      All Supplies
                    </button>
                    {Array.isArray(categories.products) && categories.products.map(category => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`px-3 py-1 rounded text-sm ${
                          selectedCategory === category 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-white/10 text-white/70 bg-white/10'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  {Array.isArray(supplies) && supplies
                    .filter(supply => selectedCategory === 'all' || supply.category === selectedCategory)
                    .slice(0, 10).map((supply, index) => (
                    <div key={index} className="p-4 bg-white/5 rounded-lg border border-blue-600/20">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="text-white font-medium">{supply.name || supply.title}</h4>
                          <p className="text-blue-200 text-sm mt-1">{supply.description}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-green-400 text-sm">💰 ${supply.price || 0}</span>
                            <span className="text-yellow-400 text-sm">📦 {supply.stock ? `${supply.stock} in stock` : 'In Stock'}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-white/60 text-xs">{supply.brand || 'Brand'}</div>
                          <div className="text-blue-300 text-xs">{supply.category || 'General'}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {!Array.isArray(supplies) || supplies.filter(supply => selectedCategory === 'all' || supply.category === selectedCategory).length === 0 && (
                    <div className="text-center py-8 text-white/60">
                      No supplies available in this category
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );

  function getAvailableTabs() {
    const tabs = [];
    
    if (permissions.includes('view_statistics')) {
      tabs.push({ id: 'overview', label: '📊 Overview' });
    }
    
    if (permissions.includes('view_users')) {
      tabs.push({ id: 'users', label: '👥 Users' });
    }
    
    if (permissions.includes('manage_promo_codes')) {
      tabs.push({ id: 'promo', label: '🎁 Promo Codes' });
    }
    
    if (permissions.includes('export_data')) {
      tabs.push({ id: 'export', label: '📁 Export' });
    }
    
    // Add customer dashboard features for all supervisors
    tabs.push({ id: 'live-bids', label: '💼 Live Bids' });
    tabs.push({ id: 'services', label: '🔧 Services' });
    tabs.push({ id: 'supplies', label: '🛒 Supplies' });
    
    // Always show at least one tab
    if (!Array.isArray(tabs) || tabs.length === 0) {
      tabs.push({ id: 'overview', label: '📊 Overview' });
    }
    
    return tabs;
  }
};

export default SupervisorDashboard;